import { X } from 'lucide-react';

import { CustomMeasurementButton } from './custom-measurement-button';

import { cn } from '@/lib/utils';
import type {
    CustomMeasurementDraft,
    DraftKind,
    DraftSlot,
} from '@/visualization/services/sizing/sizing-state-service';

const SLOTS: Record<DraftKind, { slot: DraftSlot; label: string }[]> = {
    length: [
        { slot: 'start', label: 'Start' },
        { slot: 'end', label: 'End' },
    ],
    angle: [
        { slot: 'p1', label: 'P1' },
        { slot: 'vertex', label: 'Vertex' },
        { slot: 'p2', label: 'P2' },
    ],
};

export interface CustomMeasurementDraftRowProps {
    kind: DraftKind;
    draft: CustomMeasurementDraft | null;
    isDraftComplete: boolean;
    /**
     * Complete AND computable. The draft auto-commits once this is true, so a
     * persisting complete draft with `canCommit === false` is shown as an error.
     */
    canCommit: boolean;
    onBegin: (kind: DraftKind) => void;
    onSetPoint: (slot: DraftSlot) => void;
    onCancel: () => void;
}

/**
 * Draft affordance for the "Custom Lengths" / "Custom Angles" sections.
 * Collapsed: an "+ Add custom …" button. Active (matching kind): slot buttons
 * that assign the current location; the measurement auto-commits once every
 * slot is filled. A ✕ cancels the in-progress draft.
 */
export function CustomMeasurementDraftRow({
    kind,
    draft,
    isDraftComplete,
    canCommit,
    onBegin,
    onSetPoint,
    onCancel,
}: CustomMeasurementDraftRowProps) {
    const isActive = draft?.kind === kind;

    if (!isActive) {
        return (
            <CustomMeasurementButton
                label={kind === 'length' ? 'Add length' : 'Add angle'}
                onAddCustom={() => onBegin(kind)}
            />
        );
    }

    return (
        <div className="flex flex-wrap items-center gap-1 px-2 py-1">
            {SLOTS[kind].map(({ slot, label }) => {
                const filled = draft.points[slot] != null;
                return (
                    <button
                        key={slot}
                        className={cn(
                            'rounded px-2 py-1 font-mono text-[10px] transition-colors',
                            filled
                                ? 'bg-primary text-primary-foreground'
                                : 'border border-dashed border-input text-muted-foreground hover:text-foreground'
                        )}
                        onClick={() => onSetPoint(slot)}
                        title={`Assign current location to ${label}`}
                    >
                        {filled ? '●' : '○'} {label}
                    </button>
                );
            })}
            {isDraftComplete && !canCommit && (
                <span className="basis-full px-1 text-[10px] leading-tight text-destructive">
                    {kind === 'length'
                        ? 'Start and end must lie on the same vessel.'
                        : 'These points cannot form a valid angle.'}
                </span>
            )}
            <button
                className="ml-auto rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                onClick={onCancel}
                title="Cancel"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}

export interface CustomMeasurementReassignRowProps {
    kind: DraftKind;
    measurementId: string;
    onReassign: (measurementId: string, slot: DraftSlot) => void;
}

/** Inline re-edit row shown under a selected custom length/angle. */
export function CustomMeasurementReassignRow({
    kind,
    measurementId,
    onReassign,
}: CustomMeasurementReassignRowProps) {
    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-1 px-2 pb-1 pl-7">
            <span className="text-xs text-muted-foreground">Re-assign:</span>
            {SLOTS[kind].map(({ slot, label }) => (
                <button
                    key={slot}
                    className="rounded border border-input px-2 py-1 font-mono text-[10px] text-muted-foreground hover:text-foreground"
                    onClick={() => onReassign(measurementId, slot)}
                    title={`Move ${label} to current location`}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}
